import classNames from 'classnames';
import { type FC, useMemo } from 'react';
import { Link } from 'wouter';

import Icon from 'components/Icon/Icon';
import { getMediaUrl } from 'utils/assets';
import useLayout from 'utils/layout';
import { Translation } from 'utils/translations';
import useIntl from 'utils/useIntl';

import styles from './LastWinners.module.scss';
import type { LastWinnersProps, WinnerItem, WinnersTableProps } from './types';
import useLastWinners from './useLastWinners';

const emptyStateWinners: WinnerItem[] = [
  { id: 1, game: { name: 'Gates of Olympus' }, amount: 534 },
  { id: 2, game: { name: "Texas Hold'em Bonus Poker" }, amount: 1034 },
  { id: 3, game: { name: 'Fortune Tiger' }, amount: 987 },
  { id: 4, game: { name: 'Zeus VS Hades' }, amount: 387 },
  { id: 5, game: { name: 'Zeus VS Hades' }, amount: 387 },
];

const WinnersTable: FC<WinnersTableProps> = ({ winners, formatMoney }) => (
  <table className={styles['last-winners-table']}>
    <thead>
      <tr className={styles['last-winners-header-row']}>
        <th className={styles['last-winners-game']}>
          <Translation message="game" />
        </th>
        <th className={styles['last-winners-payout']}>
          <Translation message="payout" />
        </th>
      </tr>
    </thead>
    <tbody>
      {winners.map((winner) => (
        <tr key={winner.id} className={styles['last-winners-row']}>
          <td className={styles['last-winners-game']}>
            {winner.game.id ? (
              <Link
                className={styles['last-winners-game-name']}
                to={`/game/${winner.game.id}`}
              >
                <Icon className={styles['last-winners-icon']} name="play" />{' '}
                {winner.game.name}
              </Link>
            ) : (
              <>
                <Icon className={styles['last-winners-icon']} name="play" />{' '}
                {winner.game.name}
              </>
            )}
          </td>
          <td className={styles['last-winners-amount']}>
            {formatMoney(winner.amount)}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const LastWinners: FC<LastWinnersProps> = ({ projectId, delay = 0 }) => {
  const lastWinners = useLastWinners(projectId, delay);
  const { formatMoney } = useIntl();
  const layout = useLayout();

  const items = useMemo<WinnerItem[]>(() => {
    return lastWinners.map(({ game, info: { transactionId, amount } }) => ({
      id: transactionId,
      game: { id: game.id, name: game.name },
      amount: amount,
    }));
  }, [lastWinners]);

  if (items.length === 0) {
    return (
      <div
        className={classNames(
          styles['last-winners-empty-state-wrapper'],
          styles[layout],
        )}
      >
        <div className={styles['last-winners-block']}>
          <h2 className={styles['last-winners-header']}>
            <Translation message="winnings" />
          </h2>
          <WinnersTable winners={emptyStateWinners} formatMoney={formatMoney} />
        </div>
        <div className={styles['last-winners-empty-state-img-wrapper']}>
          <img
            src={getMediaUrl('yellow/empty-winners.webp')}
            alt="empty state"
            className={styles['last-winners-empty-state-img']}
          />
          <span className={styles['last-winners-empty-state-title']}>
            <Translation message="winners_empty" />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['last-winners-block']}>
      <h2 className={styles['last-winners-header']}>
        <Translation message="winnings" />
      </h2>
      <WinnersTable winners={items} formatMoney={formatMoney} />
    </div>
  );
};

export default LastWinners;
